import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function _dateLabel(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-NZ", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch (_) { return iso; }
}

function _readingMinutes(html) {
  const words = (html || "").replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

export function BlogIndex() {
  const [posts, setPosts] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/blog/posts?limit=50`)
      .then((r) => setPosts(r.data?.posts || []))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, []);

  return (
    <div className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      <header className="border-b border-[#E4E7EA] bg-[var(--paper)]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-display font-extrabold text-sm tracking-tight">
            The Ditch Collective
          </Link>
          <Link to="/" className="text-xs text-[var(--ocean)] hover:text-[var(--ink)]">
            ← Homepage
          </Link>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-14 md:py-20">
        <p className="font-serif-italic text-[var(--ocean)] text-lg mb-4">Journal</p>
        <h1 className="font-display text-5xl sm:text-6xl font-extrabold leading-[1.02] tracking-tight">
          From the <span className="font-serif-italic font-normal">Ditch.</span>
        </h1>
        <p className="mt-5 text-[var(--ocean)] max-w-xl">
          Stories, playbooks, and settlement diaries for the trans-Tasman move.
        </p>
        {err && <p className="mt-8 text-red-600 text-sm">Couldn't load: {err}</p>}
        {posts === null && !err && <p className="mt-8 text-[var(--ocean)]">Loading…</p>}
        {posts?.length === 0 && <p className="mt-10 text-[var(--ocean)]">No posts published yet — check back soon.</p>}
        <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="blog-post-list">
          {(posts || []).map((p) => (
            <li key={p.id} data-testid={`blog-post-card-${p.slug}`}>
              <Link to={`/blog/${p.slug}`} className="group block">
                {p.hero_image_url && (
                  <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-[#E4E7EA] mb-4">
                    <img
                      src={p.hero_image_url}
                      alt={p.hero_image_alt || p.title}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                )}
                <p className="text-xs uppercase tracking-widest text-[var(--ocean)] mb-2">
                  {_dateLabel(p.published_at)}
                </p>
                <h2 className="font-display text-2xl font-extrabold leading-tight group-hover:text-[var(--ocean)] transition-colors">
                  {p.title}
                </h2>
                {p.meta_description && (
                  <p className="mt-3 text-[var(--ocean)] text-sm leading-relaxed">
                    {p.meta_description}
                  </p>
                )}
              </Link>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setPost(null); setErr("");
    axios.get(`${API}/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    // Set the page title + meta description for SEO. Kept minimal — real
    // rendering happens via crawlers hitting the /api endpoint anyway.
    document.title = `${post.title} · The Ditch Collective`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", post.meta_description || "");
  }, [post]);

  return (
    <div className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      <header className="border-b border-[#E4E7EA] bg-[var(--paper)]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-display font-extrabold text-sm tracking-tight">
            The Ditch Collective
          </Link>
          <Link to="/blog" className="text-xs text-[var(--ocean)] hover:text-[var(--ink)]">
            ← All posts
          </Link>
        </div>
      </header>

      {err && (
        <main className="max-w-3xl mx-auto px-6 py-24 text-center">
          <p className="font-serif-italic text-[var(--ocean)] text-lg mb-3">Hmm.</p>
          <h1 className="font-display text-4xl font-extrabold">
            We couldn't find that post.
          </h1>
          <Link to="/blog" className="inline-block mt-6 text-[var(--ocean)] underline">Back to the journal →</Link>
        </main>
      )}

      {!err && !post && <main className="max-w-3xl mx-auto px-6 py-24 text-[var(--ocean)]">Loading…</main>}

      {post && (
        <main className="max-w-3xl mx-auto px-6 py-14 md:py-20" data-testid="blog-post">
          <p className="text-xs uppercase tracking-widest text-[var(--ocean)] mb-4">
            {_dateLabel(post.published_at)} · {_readingMinutes(post.content_html)} min read
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-[1.05] tracking-tight" data-testid="blog-post-title">
            {post.title}
          </h1>
          {post.meta_description && (
            <p className="mt-5 text-lg text-[var(--ocean)] leading-relaxed">
              {post.meta_description}
            </p>
          )}
          {post.hero_image_url && (
            <div className="mt-10 rounded-2xl overflow-hidden bg-[#E4E7EA]">
              <img
                src={post.hero_image_url}
                alt={post.hero_image_alt || post.title}
                className="w-full h-auto"
              />
            </div>
          )}
          <article
            className="prose prose-lg max-w-none mt-12
                       prose-headings:font-display prose-headings:tracking-tight
                       prose-a:text-[var(--ocean)] prose-a:no-underline hover:prose-a:underline
                       prose-img:rounded-2xl"
            dangerouslySetInnerHTML={{ __html: post.content_html || "" }}
            data-testid="blog-post-content"
          />
          {post.infographic_image_url && (
            <figure className="mt-14 rounded-2xl overflow-hidden bg-[#E4E7EA]">
              <img src={post.infographic_image_url} alt={`${post.title} — infographic`} className="w-full h-auto" />
            </figure>
          )}
          {Array.isArray(post.keywords) && post.keywords.length > 0 && (
            <div className="mt-16 flex flex-wrap gap-2">
              {post.keywords.map((k) => (
                <span key={k} className="text-xs px-3 py-1 rounded-full bg-white border border-[#D6DCE3] text-[var(--ocean)]">
                  {k}
                </span>
              ))}
            </div>
          )}
          {post.faq_schema && Array.isArray(post.faq_schema.mainEntity) && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(post.faq_schema) }}
            />
          )}
        </main>
      )}
    </div>
  );
}
